import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Link, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tooltip, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RoundedPaper from 'shared/components/RoundedPaper';
import AgentHubService from 'services/AgentHubService';
import AgentDetail from './AgentDetail';
import { AgentState, AgentsResponse, Command, HUB_DOWN_MINUTES, HubHost } from './types';
import { AgentStatus, fmtAgo, fmtTime, PendingCommand, prUrl, StatusChip, whenItRuns } from './parts';
import TaskDialog from './TaskDialog';
import PipelineTab from './PipelineTab';
import MachineCell from './MachineCell';
import UsageTab from './UsageTab';
import QualityTab from './QualityTab';
import Areas from './Areas';
import OfficeLoading from './office/OfficeLoading';

// three.js and the models: downloaded only when someone opens the Office tab.
const OfficeTab = React.lazy(() => import('./office/OfficeTab'));

const service = new AgentHubService();
const REFRESH_MS = 5 * 60_000; // a fallback only: changes arrive live over the socket (service.watch)
const ACTION_SX = { minWidth: 0, px: 1.25 };

function HubBanner({ data }: { data: AgentsResponse }) {
  if (data.hub?.state.safetyStop) return <Alert severity="error" sx={{ mb: 2 }}><b>A hub stopped its agents: a safety hook changed.</b> {data.hub.state.safetyStop}. Check those files on that hub's Mac (<code>git status</code>, <code>git diff</code>), restore them, and restart the hub.</Alert>;
  if (data.hub?.state.billingGuard) return <Alert severity="error" sx={{ mb: 2 }}><b>A hub stopped its agents: billing guard.</b> {data.hub.state.billingGuard}. The hub runs only on the Claude subscription; an engineer must remove whatever switched it (an API key or provider setting on that hub's Mac) and restart the hub.</Alert>;
  if (!data.hub) return <Alert severity="info" sx={{ mb: 2 }}>The agent hub has not reported yet. It runs on the dedicated agent Mac (shareably/agent-hub, see its README).</Alert>;
  const { state, minutesSince } = data.hub;
  if (minutesSince > HUB_DOWN_MINUTES) return <Alert severity="error" sx={{ mb: 2 }}>The agent hub has not checked in since {fmtTime(state.at)}: no agent is running. An engineer should check the hub machines (the Hubs section below, then <code>agent-hub/setup/install.sh status</code>).</Alert>;
  if (state.pausedUntil && new Date(state.pausedUntil) > new Date()) return <Alert severity="warning" sx={{ mb: 2 }}>All agents are waiting for the Claude usage limit to reset at {fmtTime(state.pausedUntil)} ({state.pauseReason}).</Alert>;
  return null;
}

function AgentRow({ a, pending, onCommand, onTask, busy, inGroup = false }: { a: AgentState; pending?: PendingCommand; onCommand: (agent: string, c: Command) => void; onTask: (a: AgentState) => void; busy: boolean; inGroup?: boolean }) {
  const { stats, lastRun: last, failing } = a;
  const pr = prUrl(last?.subject);
  const dim = a.paused || a.status === 'retired';
  return (
    <TableRow hover sx={{ verticalAlign: 'top', bgcolor: failing ? '#FFF7F7' : dim ? '#FAFAF9' : undefined, '& > td': { py: 1.5 } }}>
      <TableCell sx={{ pl: inGroup ? 6 : undefined }}>
        <Typography component={RouterLink} to={`/agents/${a.name}`} sx={{ fontWeight: 700, color: dim ? '#585BEE' : '#4F46E5', textDecoration: 'none' }}>{a.name}</Typography>
        {a.liveActions.length > 0 && <Tooltip title={`May take these live actions: ${a.liveActions.join(', ')}`} arrow><Chip size="small" label="Live actions" sx={{ ml: 1, height: 18, fontSize: 10.5, color: '#9A3412', bgcolor: '#FFEDD5' }} /></Tooltip>}
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>{a.summary || a.description.split(/(?<=\.)\s/)[0]}</Typography>
      </TableCell>
      <TableCell>
        <Tooltip arrow title={a.status === 'draft' ? 'Draft: runs only when asked (Run now, a task, another agent), not on its schedule yet' : a.paused ? 'Paused: starts nothing new (its schedule and any waiting tasks are held) until resumed. A run already going finishes.' : ''}>
          <span><AgentStatus a={a} pending={pending} /></span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{whenItRuns(a)}</Typography>
        {!a.paused && a.nextRunAt && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', whiteSpace: 'nowrap' }}>Next {fmtTime(a.nextRunAt)}</Typography>}
        {a.queuedTasks > 0 && <Typography variant="caption" sx={{ color: a.paused ? '#B45309' : '#1E40AF', display: 'block' }}>{a.queuedTasks} task{a.queuedTasks === 1 ? '' : 's'} waiting</Typography>}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body1" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{stats?.runs7d ?? 0}</Typography>
        {!!stats?.runsToday && <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{stats.runsToday} today</Typography>}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body1" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{stats?.avgMinutes != null ? `${stats.avgMinutes} min` : '—'}</Typography>
        {stats?.minutesPerPr != null && <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{stats.minutesPerPr} min per PR</Typography>}
      </TableCell>
      <TableCell sx={{ pl: 4 }}>
        {last ? (
          <>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', whiteSpace: 'nowrap' }}>
              <StatusChip status={last.status} />
              <Typography variant="body2" title={fmtTime(last.startedAt)} sx={{ color: 'text.secondary' }}>{fmtAgo(last.startedAt)}</Typography>
            </Box>
            {pr
              ? <Link href={pr} target="_blank" rel="noreferrer" variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5, fontWeight: 600 }}>{last.subject}<OpenInNewIcon sx={{ fontSize: 14 }} /></Link>
              : <Link component={RouterLink} to={`/agents/${a.name}?run=${encodeURIComponent(last.runId)}`} variant="body2" sx={{ display: 'inline-block', mt: 0.5 }}>View run</Link>}
          </>
        ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No runs yet</Typography>}
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {a.canRunNow && <Tooltip arrow title="Run its scheduled job now, instead of waiting for the schedule"><span><Button size="small" startIcon={<PlayArrowIcon />} sx={ACTION_SX} disabled={busy || !!pending || !!a.running} onClick={() => onCommand(a.name, 'run_now')}>Run</Button></span></Tooltip>}
        {a.onDemand && <Tooltip arrow title="Give it a task in plain words"><span><Button size="small" startIcon={<AssignmentOutlinedIcon />} sx={ACTION_SX} disabled={busy} onClick={() => onTask(a)}>Task</Button></span></Tooltip>}
        {a.paused
          ? <Button size="small" variant="outlined" color="success" startIcon={<PlayCircleOutlineIcon />} disabled={busy || !!pending} onClick={() => onCommand(a.name, 'resume')} sx={{ ...ACTION_SX, ml: 0.5 }}>Resume</Button>
          : <Button size="small" variant="outlined" color="inherit" startIcon={<PauseIcon />} disabled={busy || !!pending} onClick={() => onCommand(a.name, 'pause')} sx={{ ...ACTION_SX, ml: 0.5, color: 'text.secondary', borderColor: 'divider' }}>Pause</Button>}
      </TableCell>
    </TableRow>
  );
}

const COLLAPSED_KEY = 'agents.collapsedGroups';
function readCollapsed(): string[] {
  try { return JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? '[]'); } catch { return []; }
}

/** A group's header row: name, how many agents, minutes used today, anything running. */
function GroupRow({ name, agents, open, onToggle }: { name: string; agents: AgentState[]; open: boolean; onToggle: () => void }) {
  const used = agents.reduce((n, a) => n + a.minutesToday, 0);
  const running = agents.filter(a => a.running).length;
  return (
    <TableRow onClick={onToggle} sx={{ cursor: 'pointer', bgcolor: '#F8FAFC', '&:hover': { bgcolor: '#F1F5F9' } }}>
      <TableCell colSpan={7} sx={{ py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" aria-label={open ? `Collapse ${name}` : `Expand ${name}`} aria-expanded={open}>{open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}</IconButton>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{name}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {agents.length} agent{agents.length === 1 ? '' : 's'} · {Math.round(used * 10) / 10} min today{running ? ` · ${running} running` : ''}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
}

/** /agents — every agent on the agent Mac; /agents/:name — one agent's runs and what each did. */
export default function AgentHub() {
  const { name } = useParams<{ name?: string }>();
  const [data, setData] = useState<AgentsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [tasking, setTasking] = useState<AgentState | null>(null);
  // Which groups are collapsed: remembered in this browser only.
  const [collapsed, setCollapsed] = useState<string[]>(readCollapsed);
  const toggleGroup = (g: string) => setCollapsed(c => {
    const next = c.includes(g) ? c.filter(x => x !== g) : [...c, g];
    try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next)); } catch { /* private window: not remembered */ }
    return next;
  });
  const [tab, setTab] = useState<'agents' | 'pipeline' | 'usage' | 'quality' | 'office'>(() => { const t = new URLSearchParams(window.location.search).get('tab'); return t === 'pipeline' || t === 'usage' || t === 'quality' || t === 'office' ? t : 'agents'; });

  const load = useCallback(() => {
    service.agents()
      .then(d => { setData(d); setError(null); })
      .catch(e => setError(e.message));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, REFRESH_MS); return () => clearInterval(t); }, [load]);
  // Live: every change to the hub's state (a run starting or ending, a pause) shows as it happens.
  useEffect(() => {
    let stop: (() => void) | null = null;
    let gone = false;
    service.watch(hub => { if (hub) setData(d => (d ? { ...d, hub } : { hub })); })
      .then(off => { if (gone) off(); else stop = off; })
      .catch(e => setError(e.message));
    return () => { gone = true; stop?.(); };
  }, []);

  // A click waits for the hub's answer (a second or two, through Firebase), which carries its new state.
  const [pending, setPending] = useState<Record<string, PendingCommand>>({});
  const onCommand = (agent: string, command: Command) => {
    setBusy(true);
    setPending(p => ({ ...p, [agent]: { command, at: Date.now() } }));
    service.command(agent, command)
      .then(hub => { if (hub) setData(d => (d ? { ...d, hub } : { hub })); setNotice(null); })
      .catch(e => setError(`${agent}: ${command.replace('_', ' ')} failed: ${e.message}`)) // stays up across pages, so say which agent
      .finally(() => { setBusy(false); setPending(p => { const { [agent]: _done, ...rest } = p; return rest; }); });
  };

  const agentsList = data?.hub?.state.agents ?? [];
  const failing = agentsList.filter(a => a.failing).map(a => a.name);

  if (name) return <AgentDetail name={name} agent={data?.hub?.state.agents.find(a => a.name === name) ?? null} pending={pending[name]} onCommand={onCommand} busy={busy} notice={notice} onNotice={setNotice} commandError={error} onDismissError={() => setError(null)} />;
  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>Agents</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Claude Code agents on the agent hub. They run as often as the work needs; each run's effort is recorded. Open one for its runs and settings.
        {data?.hub ? ` ${data.hub.state.maxConcurrent} at a time, last check-in ${fmtTime(data.hub.state.at)}.` : ''}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {failing.length > 0 && <Alert severity="error" sx={{ mb: 2 }}>Failing two runs in a row: {failing.join(', ')}.</Alert>}
      {!data && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>}
      {data && <HubBanner data={data} />}
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="agents" label="Agents" />
        <Tab value="pipeline" label="PR pipeline & approvals" />
        <Tab value="usage" label="Usage" />
        <Tab value="quality" label="Quality" />
        <Tab value="office" label="Office" />
      </Tabs>
      {tab === 'pipeline' && <PipelineTab />}
      {tab === 'usage' && <UsageTab />}
      {tab === 'quality' && <QualityTab />}
      {tab === 'office' && <Suspense fallback={<OfficeLoading />}><OfficeTab data={data} pending={pending} busy={busy} onCommand={onCommand} onTask={setTasking} /></Suspense>}
      <TaskDialog agent={tasking} onClose={() => setTasking(null)} onSent={setNotice} />
      {tab === 'agents' && data?.hub && (
        <RoundedPaper sx={{ p: 0, overflowX: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1150 }}>
            {/* Fixed columns, so headers stay on one line and numbers line up across groups. */}
            <colgroup><col style={{ width: '26%' }} /><col style={{ width: '10%' }} /><col style={{ width: '13%' }} /><col style={{ width: '7%' }} /><col style={{ width: '9%' }} /><col style={{ width: '14%' }} /><col style={{ width: '21%' }} /></colgroup>
            <TableHead sx={{ '& th': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}><TableRow><TableCell>Agent</TableCell><TableCell>Status</TableCell><TableCell>Schedule</TableCell><TableCell align="right">Runs, 7d</TableCell><TableCell align="right">Avg run</TableCell><TableCell sx={{ pl: 4 }}>Last run</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {(() => {
                const row = (a: AgentState, inGroup = false) => <AgentRow key={a.name} a={a} inGroup={inGroup} pending={pending[a.name]} onCommand={onCommand} onTask={setTasking} busy={busy} />;
                const others = data.hub.state.agents.filter(a => !a.group);
                const agents = data.hub.state.agents;
                const groups = Array.from(new Set(agents.map(a => a.group).filter((g): g is string => !!g))).sort();
                return (
                  <>
                    {groups.map(g => {
                      const members = agents.filter(a => a.group === g);
                      const open = !collapsed.includes(g);
                      return (
                        <React.Fragment key={g}>
                          <GroupRow name={g} agents={members} open={open} onToggle={() => toggleGroup(g)} />
                          {open && members.map(a => row(a, true))}
                        </React.Fragment>
                      );
                    })}
                    {groups.length > 0 && others.length > 0 && (
                      <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                        <TableCell colSpan={7} sx={{ py: 1.25 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>Other agents</Typography></TableCell>
                      </TableRow>
                    )}
                    {others.map(a => row(a))}
                  </>
                );
              })()}
            </TableBody>
          </Table>
        </RoundedPaper>
      )}
      {tab === 'agents' && data?.hub && <RoundedPaper sx={{ mt: 3 }}><Areas /></RoundedPaper>}
      {tab === 'agents' && data?.hub && <HubsSection />}
    </Box>
  );
}

/** Engineers only, folded away: each hub machine on its own. Everyone else sees the hubs as one. */
function HubsSection() {
  const [open, setOpen] = useState(false);
  const [hosts, setHosts] = useState<HubHost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setError(null);
    setHosts(null);
    service.hosts().then(setHosts).catch(e => setError(e.message));
  }, [open]);
  return (
    <Box sx={{ mt: 3 }}>
      <Button size="small" onClick={() => setOpen(o => !o)} startIcon={open ? <ExpandMoreIcon /> : <ChevronRightIcon />} sx={{ color: 'text.secondary', textTransform: 'none' }} aria-expanded={open}>
        Hubs (for engineers)
      </Button>
      {open && (
        <RoundedPaper sx={{ p: 0, mt: 1, overflowX: 'auto' }}>
          {error && <Alert severity="error" sx={{ m: 2 }}>Hubs not loaded: {error}</Alert>}
          {!hosts && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>}
          {hosts && (
            <Table size="small">
              <TableHead><TableRow><TableCell>Hub</TableCell><TableCell>Status</TableCell><TableCell>Last check-in</TableCell><TableCell>Version</TableCell><TableCell align="right">Runs</TableCell><TableCell>Machine</TableCell><TableCell>Running</TableCell></TableRow></TableHead>
              <TableBody>
                {hosts.map(h => {
                  const down = h.minutesSince > HUB_DOWN_MINUTES;
                  const stop = h.safetyStop ? `safety stop: ${h.safetyStop}` : h.billingGuard ? `billing guard: ${h.billingGuard}` : null;
                  return (
                    <TableRow key={h.host}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{h.host}</TableCell>
                      <TableCell><Chip size="small" label={down ? 'down' : stop ? 'stopped' : 'up'} color={down || stop ? 'error' : 'success'} variant="outlined" />{stop && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{stop}</Typography>}</TableCell>
                      <TableCell>{fmtAgo(h.at)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{h.version ?? '—'}</TableCell>
                      <TableCell align="right">{h.runs ?? '—'} / {h.maxConcurrent}</TableCell>
                      <TableCell><MachineCell host={h} /></TableCell>
                      <TableCell>{h.running.join(', ') || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </RoundedPaper>
      )}
    </Box>
  );
}
